import { useState, useEffect } from 'react';
import { ApolloCache, useMutation, useQuery } from '@apollo/client';
import {
  CRIAR_SEGUIDOR_MUTATION,
  DELETAR_SEGUIDOR_MUTATION,
  CriarSeguidorResponse,
  SEGUIDOR_USUARIO_TORNEIO,
  SeguidorUsuarioTorneioData,
  SeguidorUsuarioTorneioVars,
} from '../services/seguidorService';

/**
 * Remove do cache do Apollo a lista de perfis seguidos.
 *
 * Existe porque seguir/deixar de seguir muda o resultado de `seguidosPorAtletaId`
 * e as mutações não tinham como o Apollo saber disso: elas devolvem o `Seguidor`
 * criado/removido, não a lista. Sem esta invalidação, `AtletaSeguindoTab` (e o
 * preview de seguidos da sidebar, que consome a MESMA query) montam com
 * `fetchPolicy` padrão `cache-first` e servem o resultado anterior — o usuário
 * seguia uma liga, navegava até a aba e via a lista velha (achado de QA do owner,
 * 2026-08-31).
 *
 * Invalidar no ponto da MUTAÇÃO, e não na tela, é deliberado: conserta todo
 * consumidor de uma vez, inclusive os que ainda não existem. A alternativa
 * (`cache-and-network` em cada tela) exigiria que cada consumidor novo lembrasse
 * de repetir o cuidado.
 *
 * `refetchQueries` NÃO resolveria este caso: ele só age sobre queries ATIVAS, e
 * no momento de seguir a aba costuma nem estar montada — o usuário está na página
 * da liga. Evitar do cache faz a próxima montagem buscar da rede.
 */
function invalidarSeguidosNoCache(cache: ApolloCache<unknown>): void {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'seguidosPorAtletaId' });
  cache.gc();
}


interface UseSeguirTorneioParams {
  seguidorUserId: string;
  torneioId: string;
  skip?: boolean;
}

interface UseSeguirTorneioReturn {
  followId: string | null;
  loading: boolean;
  error: string;
  handleSeguir: () => Promise<void>;
  isFollowing: boolean;
}

export const useSeguirTorneio = ({
  seguidorUserId,
  torneioId,
  skip = false,
}: UseSeguirTorneioParams): UseSeguirTorneioReturn => {
  const [seguir, { loading: loadingSeguir }] = useMutation<{
    criarSeguidor: CriarSeguidorResponse;
  }, { input: any }>(CRIAR_SEGUIDOR_MUTATION, {
    update: (cache) => invalidarSeguidosNoCache(cache),
  });
  const [deletar, { loading: loadingDeletar }] = useMutation(DELETAR_SEGUIDOR_MUTATION, {
    update: (cache) => invalidarSeguidosNoCache(cache),
  });
  const [followId, setFollowId] = useState<string | null>(null);
  const [erro, setErro] = useState('');

  const { data: seguidorData } = useQuery<SeguidorUsuarioTorneioData, SeguidorUsuarioTorneioVars>(
    SEGUIDOR_USUARIO_TORNEIO,
    {
      variables: { seguidorId: seguidorUserId, seguidoTorneioId: torneioId },
      skip: skip || !seguidorUserId || !torneioId,
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    if (seguidorData?.seguidorPorSeguidorIdSeguidoTorneioId?.id) {
      setFollowId(seguidorData.seguidorPorSeguidorIdSeguidoTorneioId.id);
    } else {
      setFollowId(null);
    }
  }, [seguidorData]);

  const handleSeguir = async () => {
    if (!seguidorUserId || !torneioId) return;
    setErro('');
    try {
      if (followId) {
        await deletar({ variables: { id: followId } });
        setFollowId(null);
      } else {
        setFollowId('temp');
        const { data } = await seguir({
          variables: {
            input: {
              seguidorId: seguidorUserId,
              seguidoAtletaId: null,
              seguidoTorneioId: torneioId,
              seguidoLigaId: null,
            },
          },
        });
        const id = data?.criarSeguidor.id;
        if (id) setFollowId(id);
        else setFollowId(null);
      }
    } catch (e) {
      setErro('Não foi possível atualizar. Tente novamente.');
      if (!followId || followId === 'temp') setFollowId(null);
    }
  };

  return {
    followId,
    loading: loadingSeguir || loadingDeletar || followId === 'temp',
    error: erro,
    handleSeguir,
    isFollowing: !!followId && followId !== 'temp',
  };
};

export default useSeguirTorneio;
